import * as React from "react";
import { ChevronDown, ChevronRight, FolderOpen } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

export interface TranslationLeaf {
  key: string;
  preview: string;
  status?: "missing" | "stale" | "ok";
}

export interface TranslationBranch {
  name: string;
  branches?: TranslationBranch[];
  leaves?: TranslationLeaf[];
  counts?: { missing?: number; stale?: number; total?: number };
}

export interface TranslationTreeProps {
  branches: TranslationBranch[];
  onLeafSelect?: (key: string) => void;
}

function Counts({ counts }: { counts?: TranslationBranch["counts"] }) {
  if (!counts) return null;
  return (
    <div className="ml-auto flex items-center gap-1">
      {counts.missing ? <Badge variant="soft">{counts.missing} missend</Badge> : null}
      {counts.stale ? <Badge variant="status-todo">{counts.stale} verouderd</Badge> : null}
      {counts.total ? <Badge>{counts.total} sleutels</Badge> : null}
    </div>
  );
}

function Branch({
  branch,
  depth = 0,
  onLeafSelect,
}: {
  branch: TranslationBranch;
  depth?: number;
  onLeafSelect?: (key: string) => void;
}) {
  const [open, setOpen] = React.useState(depth < 1);
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 rounded-2 px-2 py-1 text-left hover:bg-surface-1"
        style={{ paddingLeft: 8 + depth * 16 }}
      >
        <Chevron size={14} className="text-ink-500" />
        <FolderOpen size={14} className="text-ink-500" />
        <span className="text-[13px] font-medium text-ink-900">{branch.name}</span>
        <Counts counts={branch.counts} />
      </button>
      {open ? (
        <div>
          {branch.leaves?.map((l) => (
            <button
              key={l.key}
              type="button"
              onClick={() => onLeafSelect?.(l.key)}
              className="flex w-full items-center gap-2 rounded-2 px-2 py-1 text-left hover:bg-surface-1"
              style={{ paddingLeft: 16 + (depth + 1) * 16 }}
            >
              <span className="inline-block size-1.5 rounded-full bg-ink-300" />
              <span className="font-mono text-[12px] text-ink-700">{l.key}</span>
              <span className="ml-3 truncate italic text-[12px] text-ink-500">{l.preview}</span>
            </button>
          ))}
          {branch.branches?.map((b) => (
            <Branch key={b.name} branch={b} depth={depth + 1} onLeafSelect={onLeafSelect} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export function TranslationTree({ branches, onLeafSelect }: TranslationTreeProps) {
  return (
    <div className={cn("rounded-5 border border-line bg-surface p-2")}>
      {branches.map((b) => (
        <Branch key={b.name} branch={b} onLeafSelect={onLeafSelect} />
      ))}
    </div>
  );
}
